import { onlyAlnum } from '@divipay/br-documents';
import { Carnet, CarnetItem, CarnetItemStatus, CarnetStatus, CreateCarnetRequest } from 'types/Carnet';
import { maskDocument } from 'utils/document';
import { lateChargesBody, lateFormOf } from 'utils/lateCharges';
import { formatCurrency } from 'utils/number';
import { CreateCarnetValues, MAX_INSTALLMENTS, MIN_INSTALLMENTS } from './schema';

/** Chave da lista no react-query: emissão, retry e detalhe a invalidam. */
export const CARNETS_QUERY_KEY = 'findCarnets';

type StatusMeta = { label: string; colorScheme: string };

const CARNET_STATUS: Record<CarnetStatus, StatusMeta> = {
  PENDING: { label: 'Na fila', colorScheme: 'orange' },
  PROCESSING: { label: 'Emitindo', colorScheme: 'blue' },
  COMPLETED: { label: 'Pronto', colorScheme: 'green' },
  PARTIALLY_COMPLETED: { label: 'Parcialmente emitido', colorScheme: 'yellow' },
  FAILED: { label: 'Falhou', colorScheme: 'red' },
  // Cinza, não vermelho: cancelar é decisão do lojista (pelo app ou pelo admin),
  // não falha.
  CANCELED: { label: 'Cancelado', colorScheme: 'gray' },
};

const ITEM_STATUS: Record<CarnetItemStatus, StatusMeta> = {
  PENDING: { label: 'Na fila', colorScheme: 'orange' },
  PROCESSING: { label: 'Emitindo', colorScheme: 'blue' },
  SUCCEEDED: { label: 'Emitida', colorScheme: 'green' },
  FAILED: { label: 'Falhou', colorScheme: 'red' },
  SKIPPED_DUPLICATE: { label: 'Já emitida', colorScheme: 'gray' },
  CANCELED: { label: 'Cancelada', colorScheme: 'gray' },
};

/**
 * Estado que este painel não conhece aparece cru, em cinza. O servidor pode
 * ganhar um estado novo antes de o painel aberto no navegador ser recarregado.
 */
const unknownStatus = (status?: string | null): StatusMeta => ({ label: status || '—', colorScheme: 'gray' });

export const carnetStatusMeta = (status?: string | null): StatusMeta =>
  CARNET_STATUS[status as CarnetStatus] ?? unknownStatus(status);

export const carnetItemStatusMeta = (status?: string | null): StatusMeta =>
  ITEM_STATUS[status as CarnetItemStatus] ?? unknownStatus(status);

/** Cancelado é estado final: o servidor recusa o retry com 409. */
export const canRetryCarnet = (carnet: Pick<Carnet, 'status' | 'failedItems'>): boolean =>
  carnet.failedItems > 0 && carnet.status !== 'CANCELED';

/**
 * "O PDF traz as parcelas já emitidas, baixe de novo depois": só enquanto ainda
 * há parcela por emitir. Num carnê cancelado as que faltavam foram
 * interrompidas e não vão chegar.
 */
export const showsPartialPdfHint = (carnet: Pick<Carnet, 'status' | 'succeededItems' | 'totalItems'>): boolean =>
  carnet.status !== 'CANCELED' && carnet.succeededItems > 0 && carnet.succeededItems < carnet.totalItems;

const IN_FLIGHT = ['PENDING', 'PROCESSING'];

/**
 * O lote só muda enquanto está na fila ou emitindo: é quando o polling vale.
 * Definido pelo que ESTÁ em andamento, e não pela lista de estados finais, para
 * um estado final desconhecido não deixar a tela consultando para sempre.
 */
export const isCarnetSettled = (status?: string | null): boolean => !!status && !IN_FLIGHT.includes(status);

/**
 * As parcelas nascem todas PENDING e só chegam a um estado final pelo
 * processamento — então o que ainda está em andamento é o que diz se vale
 * continuar lendo.
 */
export const hasItemsInFlight = (items?: Array<Pick<CarnetItem, 'status'>> | null): boolean =>
  !!items?.some((item) => IN_FLIGHT.includes(item.status));

/** De quanto em quanto tempo a tela relê o servidor enquanto o carnê emite. */
const CARNET_POLL_MS = 3000;

/**
 * Intervalo de releitura do lote. Segue lendo enquanto o que está na tela ainda
 * está em andamento — inclusive depois de uma leitura que falhou: uma queda
 * passageira (deploy, rede) no meio da emissão não pode congelar a tela em
 * "Emitindo" até a pessoa recarregar a página. Só desiste quando nunca chegou
 * dado nenhum (404, sem permissão): aí repetir não muda nada, e o botão de
 * tentar novamente fica com a pessoa.
 */
export const carnetRefetchInterval = (
  carnet: Pick<Carnet, 'status'> | undefined | null,
  failed: boolean,
): number | false => {
  if (!carnet) return failed ? false : CARNET_POLL_MS;
  return isCarnetSettled(carnet.status) ? false : CARNET_POLL_MS;
};

/** O mesmo, para as parcelas: decide pelos próprios dados, não pelo status do lote. */
export const carnetItemsRefetchInterval = (
  items: Array<Pick<CarnetItem, 'status'>> | undefined | null,
  failed: boolean,
): number | false => {
  if (!items) return failed ? false : CARNET_POLL_MS;
  return hasItemsInFlight(items) ? CARNET_POLL_MS : false;
};

/** Teto de `limit` da listagem de lotes do servidor (ListBatchesDto, @Max(100)). */
const MAX_CARNETS_PAGE_SIZE = 100;

/**
 * A tabela oferece "Exibir 250" e "Exibir 500"; acima do teto o servidor
 * responde 400 e a lista ficava vazia, sem explicação.
 */
export const carnetsPageSize = (limit: number): number => Math.min(limit, MAX_CARNETS_PAGE_SIZE);

const ISSUED_ITEM_STATUSES = ['SUCCEEDED', 'SKIPPED_DUPLICATE'];

/**
 * A cobrança de uma parcela EMITIDA, que o painel pode abrir. O servidor também
 * grava o chargeId em parcela FAILED (a cobrança criada antes do erro, para o
 * admin corrigir a tarifa) e o mantém quando a parcela volta à fila: abrir essa
 * cobrança mostraria um boleto de verdade preso a uma parcela que diz ter
 * falhado.
 */
export const issuedChargeId = (item: Pick<CarnetItem, 'status' | 'chargeId'>): string | undefined =>
  ISSUED_ITEM_STATUSES.includes(item.status) ? item.chargeId || undefined : undefined;

/**
 * yyyy-MM-dd → dd/MM/yyyy sem passar por Date: new Date('2026-11-10') é
 * meia-noite UTC e, no Brasil, mostraria o dia 9.
 */
export const formatIsoDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const [year, month, day] = iso.slice(0, 10).split('-');
  if (!year || !month || !day) return '—';
  return `${day}/${month}/${year}`;
};

/** "12× R$ 150,00". A tela passa `maskCurrency` para respeitar o modo privacidade. */
export const carnetInstallmentsLabel = (
  carnet: Pick<Carnet, 'totalItems' | 'metadata'>,
  format: (value: number) => string = formatCurrency,
): string => `${carnet.totalItems}× ${format(carnet.metadata?.amountPerInstallment ?? 0)}`;

/**
 * Pagador do carnê, que é o que diferencia dez carnês "Mensalidade" na lista.
 * `null` quando não há nome a mostrar.
 */
export const carnetPayer = (
  carnet: Pick<Carnet, 'payerName' | 'payerDocument'>,
): { name: string; document: string } | null => {
  const name = carnet.payerName?.trim();
  if (!name) return null;
  return { name, document: maskDocument(carnet.payerDocument ?? undefined) };
};

/** Teto do `search` no servidor (ListBatchesDto). */
const MAX_CARNET_SEARCH_LENGTH = 100;

/**
 * O termo da busca como vai para o servidor: descrição, nome ou documento do
 * pagador. Vazio vira `undefined` (sem filtro), e passar do teto do servidor
 * responderia 400, esvaziando a lista sem explicação.
 */
export const carnetSearchTerm = (value?: string | null): string | undefined => {
  const term = value?.trim().slice(0, MAX_CARNET_SEARCH_LENGTH);
  return term || undefined;
};

/**
 * Último recurso, só para servidor que ainda não manda `billetProtestFee`. Quem
 * debita o lojista é a taxa BILLET_PROTEST do plano e, na falta dela,
 * DEFAULT_PROTEST_FEE em apps/server/src/charges/carnet/protest-fee.ts. Se
 * divergirem, o servidor vale.
 */
const FALLBACK_PROTEST_FEE_PER_BILLET = 14.9;

export const protestFeePerBillet = (fee?: number | string | null): number => {
  const parsed = Number(fee ?? FALLBACK_PROTEST_FEE_PER_BILLET);
  return Number.isFinite(parsed) ? parsed : FALLBACK_PROTEST_FEE_PER_BILLET;
};

/**
 * O preço vai no texto de quem liga a opção porque a tarifa multiplica rápido:
 * a R$ 14,90, num carnê de 24 o lojista contrata R$ 357,60. Sem quantidade
 * válida não há total a mostrar.
 */
export const protestDescription = (feePerBillet: number, installments: number): string => {
  const validInstallments =
    Number.isInteger(installments) && installments >= MIN_INSTALLMENTS && installments <= MAX_INSTALLMENTS;
  return (
    `${formatCurrency(feePerBillet)} por boleto protestado` +
    (validInstallments
      ? ` — até ${formatCurrency(feePerBillet * installments)} em ${installments} parcelas`
      : '') +
    '. Cobrado de você apenas nos boletos que forem efetivamente protestados. ' +
    'O valor do boleto para o pagador não muda.'
  );
};

/**
 * O corpo de POST /charges/billet-carnet a partir do formulário validado.
 * Opcional vazio não vai: no servidor, ausente herda o padrão da conta e zero
 * isenta. `protestAvailable` é reavaliado aqui porque um refetch pode tirar a
 * config da conta depois de o lojista ter marcado o toggle.
 */
export const buildCarnetBody = (
  values: CreateCarnetValues,
  options: { protestAvailable: boolean },
): CreateCarnetRequest => {
  return {
    amountPerInstallment: values.amountPerInstallment,
    installments: values.installments,
    firstDueDate: values.firstDueDate,
    description: values.description,
    payer: {
      name: values.payerName,
      // Sem máscara, e alfanumérico por causa do CNPJ com letras.
      document: onlyAlnum(values.payerDocument),
      ...(values.payerEmail ? { email: values.payerEmail } : {}),
    },
    ...lateChargesBody(lateFormOf(values)),
    ...(options.protestAvailable && values.withProtest ? { withProtest: true } : {}),
  };
};

type CryptoLike = {
  randomUUID?: () => string;
  getRandomValues: (array: Uint8Array) => Uint8Array;
};

/**
 * Chave de idempotência da emissão. A tela gera UMA por formulário aberto e a
 * reenvia em toda tentativa: um clique duplo ou um retry de rede devolve o
 * mesmo lote, em vez de registrar outros N boletos tarifados.
 *
 * `randomUUID` só existe em contexto seguro (https); o resto monta um UUID v4
 * com `getRandomValues`.
 */
export const newIdempotencyKey = (cryptoImpl: CryptoLike = window.crypto as unknown as CryptoLike): string => {
  if (typeof cryptoImpl.randomUUID === 'function') return cryptoImpl.randomUUID();

  const bytes = cryptoImpl.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // versão 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};

/** A `message` do corpo de erro do servidor. O Nest manda array em erro de validação. */
export const apiErrorMessage = (err: unknown, fallback: string): string => {
  const message = (err as { response?: { data?: { message?: unknown } } } | undefined)?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ') || fallback;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Como `apiErrorMessage`, para chamada feita com `responseType: 'blob'`: ali o
 * corpo de ERRO também chega como Blob e precisa ser lido antes. É a mensagem
 * que diz qual parcela não pôde ser desenhada no PDF.
 */
export const blobErrorMessage = async (err: unknown, fallback: string): Promise<string> => {
  const data = (err as { response?: { data?: { text?: () => Promise<string> } } } | undefined)?.response?.data;
  if (data && typeof data.text === 'function') {
    try {
      return apiErrorMessage({ response: { data: JSON.parse(await data.text()) } }, fallback);
    } catch {
      return fallback;
    }
  }
  return apiErrorMessage(err, fallback);
};
